import * as THREE from "three";
import { gsap } from "gsap";
import { World } from "./World";
import { KafkaStep } from '../constants';
import { OutlineMaterial } from '../customMaterials/OutlineMaterial';
import { HatchingMaterial } from '../customMaterials/HatchingMaterial';

const commonParams = {
    color: 0xffffff,
    transparent: false,
    fog: true,
    hatchingScale: 30,
    mixDiffuse: 0.5,
    shadowDarkness: 0.1,
    blackLevel: 0,
    whiteLevel: 1,
}

const Objects = {
    persos1: ["Joseph", "agent1", "agent2", "gouvernante", "siamois", "shoes", "shoes2"],
    persos2: ["voisin1", "voisin2", "voisine1", "voisine2", "voisine3"],
    deco: ["cup", "plate"],
    papers: [0, 2, 3, 4, 5, 6].map(i => `paper${i || ""}`),
}

export class KafkaWorld extends World {
    constructor({ name, model, assets }) {
        super({ name, model, assets })
        this.camera.near = 0.1;
        this.camera.far = 100;
        this.titleBrightness = 1;
        this.titleDisplace = -0.1;
        this.titleScale = 0.9;
    }

    processModel() {
        this.createMaterials()
        super.processModel();
        this.createLights()

        const { persos1, persos2, deco, papers } = Objects

        // ROOM
        this.room = this.model.scene.getObjectByName("room")
        this.matRoom.map = this.room.material.map;
        this.room.material = this.matRoom;

        let strokeRoom = this.room.clone()
        strokeRoom.children = []
        strokeRoom.material = this.outlineMatBlack;
        this.scene.add(strokeRoom)

        // SKY
        this.sky = this.model.scene.getObjectByName("sky")
        this.matSky.map = this.sky.material.map
        this.sky.material = this.matSky

        // BUILDINGS
        const buildings = this.model.scene.getObjectByName("buildings")
        this.matBuildings.map = buildings.material.map;
        buildings.material = this.matBuildings;

        const strokeBuildings = buildings.clone()
        strokeBuildings.children = []
        strokeBuildings.material = this.outlineMatBlack;
        this.scene.add(strokeBuildings)

        // DECO Objects
        for (let i = 0; i < deco.length; i++) {
            let objectName = deco[i]
            let object = this.model.scene.getObjectByName(objectName)
            if (i == 0) this.matDeco.map = object.material.map;
            object.material = this.matDeco;

            let strokeObject = object.clone()
            strokeObject.children = []
            strokeObject.material = this.outlineMatBlack;
            this.scene.add(strokeObject)
        }

        // PERSOS1
        for (let i = 0; i < persos1.length; i++) {

            let objectName = persos1[i]
            let object = this.model.scene.getObjectByName(objectName)
            if (i === 0) {
                this.matPersos1.map = object.material.map;
            }
            object.material = this.matPersos1;

            if (objectName === "Joseph") {
                this.joseph = object
                this.joseph.frustumCulled = false;
            }

            if (objectName !== "Joseph" && objectName !== "gouvernante" && objectName !== "shoes") {
                let strokeObject = object.clone()
                strokeObject.children = []
                strokeObject.material = this.outlineMatBlack;
                this.scene.add(strokeObject)
            }

            if (objectName === "shoes") {
                let strokeObject = object.clone()
                strokeObject.children = []
                strokeObject.material = this.outlineMatWhite;
                this.scene.add(strokeObject)
            }
        }

        // PERSOS2
        for (let i = 0; i < persos2.length; i++) {

            let objectName = persos2[i];
            let object = this.model.scene.getObjectByName(objectName)

            if (i === 0) {
                this.matPersos2.map = object.material.map;
            }

            object.material = this.matPersos2;

            let strokeObject = object.clone()
            strokeObject.children = []
            strokeObject.material = this.outlineMatWhite;
            this.scene.add(strokeObject)
        }


        // Is it worth moving the papers out to a separate manager?

        // PAPERS
        for (let i = 0; i < papers.length; i++) {

            const objectName = papers[i] // better to use const for things that don't change
            const object = this.model.scene.getObjectByName(objectName)

            if (i === 0) {
                this.matPaper.map = object.material.map;
            }
            object.material = this.matPaper;

            // Assign all named "paper" objects to equivalent instance variables
            if (objectName.substring(0, 5) === "paper") {
                this[objectName] = object; // eg. this.paper3 = the object named "paper3"
            }
        }

        // Neighbor
        this.targetNeighbor = this.scene.getObjectByName("target8");
        this.pointLightNeighbor.position.set(this.targetNeighbor.position.x, this.targetNeighbor.position.y, this.targetNeighbor.position.z)
        this.neighbor1 = this.scene.getObjectByName("voisine1");
        //*
        this.animation = this.model.animations[0];
        this.mixer = new THREE.AnimationMixer(this.model.scene);
        this.mixer.clipAction(this.animation).play();
        this.mixer.update(0)
        //*/
    }

    loop({ time, deltaTime }) {
        super.loop({ time, deltaTime });

        const sin1 = Math.sin(time * 0.65);
        const sin2 = Math.sin(time * 0.31);
        const sin3 = Math.sin(time * 1.71);
        const sin4 = Math.sin(time * 5.31);
        const sin5 = Math.sin(time * 12.51);
        const cos1 = Math.cos(time * 1.31);
        const cos2 = Math.cos(time * 0.32);
        const cos3 = Math.cos(time * 7.32);

        // adding a subtle movement to the target to simulate eyes movements


        this.outlineMatBlack.uniforms.time.value = time * 20;
        this.outlineMatWhite.uniforms.time.value = time * 20;

        // PointLights room and joseph Animation
        const { HOUSEKEEPER, SIAMESE, AERIAL_VIEW, TITLES, ROOM_CLOSE, NEIGHBOUR, ROOM_FAR, WALL } = KafkaStep;

        if (this.currentStep >= ROOM_CLOSE && this.currentStep <= ROOM_FAR) {

            // Tween rather than set
            gsap.to(this.pointLightRoom, {
                intensity: this.pointLightRoomIntensity,
                duration: 3
            });

            this.pointLightRoom.position.set(
                cos2 * 0.5,
                -.5 + sin1 * 1.2,
                .5 + sin2 * 0.4,
            )

            if (this.mixer) {
                this.mixer.update(deltaTime);
            }
        } else {
            this.pointLightRoom.intensity = 0;
        }


        // Paper

        if ([HOUSEKEEPER, SIAMESE, AERIAL_VIEW].find(i => i === this.currentStep)) {
            this.paper.rotation.x += sin1 * 0.02;
            this.paper.rotation.y += cos2 * 0.015;
            this.paper.position.x = 1 + sin2 * 0.2 + cos1 * 0.1
            this.paper.position.z = this.targets[4].position.z - 1 + sin2 * 0.3 + cos1 * 0.4
            this.pointLightPaper.intensity = this.pointLightPaperIntensity;
            this.pointLightPaper.position.x = this.targets[4].position.x - 0.2 + Math.abs(sin1) * 0.2;
            this.pointLightPaper.position.y = this.targets[4].position.y + cos1 * 1.0;
            this.pointLightPaper.position.z = this.targets[4].position.z + cos2 * .5;
        } else {
            this.pointLightPaper.intensity = 0;
        }

        // Paper3

        if (this.currentStep === TITLES || this.currentStep === ROOM_CLOSE || this.currentStep === NEIGHBOUR) {

            this.paper3.position.x = sin2 * 2 + cos1 * 0.5
            this.paper3.position.y = cos2 * 1.5 + sin1 * 0.3
            this.paper3.position.z = 56 + cos1 * 1 + sin3 * 0.6

            this.paper3.rotation.x = sin2 * 2 + cos1 * 0.5
            this.paper3.rotation.y = cos2 * 1.5 + sin3 * 0.3
            this.paper3.rotation.z = cos1 * 1 + sin3 * 0.6
        }

        if (this.currentStep === ROOM_FAR || this.currentStep === WALL) {
            this.paper5.rotation.z = sin4 * 0.07 + cos3 * 0.05 + sin5 * 0.03;
            this.paper5.position.z = 1.70483 + sin1 * 0.03
        }

        // Paper4 and neighbor light


        if (this.currentStep === NEIGHBOUR) {
            this.paper4.rotation.set(
                sin2 * 2 + cos2 * 0.5,
                cos2 * 1.5 + sin3 * 0.3,
                cos2 * 1 + sin2 * 0.6
            )

            this.paper4.position.x = -5.5 + sin2 * cos1 * 0.5 + cos2 * 0.2
            this.paper4.position.z = -.5 + this.targetNeighbor.position.z + sin1 * 0.2 + cos1 * 0.2

            const { x, y, z } = this.targetNeighbor.position;

            this.pointLightNeighbor.position.set(
                x + 1 + Math.abs(sin2 * 0.2),
                y + sin1,
                z + cos1 * 0.8
            )
        }

    }

    createLights() {
        // Lights
        this.pointLightPaperIntensity = 2.5;
        this.pointLightRoomIntensity = 1.5;

        this.globalLight = new THREE.AmbientLight(0xffffff, .16);
        this.pointLightRoom = new THREE.PointLight(0xffffff, this.pointLightRoomIntensity, 2, .5)
        this.pointLightPaper = new THREE.PointLight(0xffffff, this.pointLightPaperIntensity, 1.5, .5)
        this.pointLightNeighbor = new THREE.PointLight(0xffffff, 2, 1.5, .5)

        this.pointLightHelperRoom = new THREE.PointLightHelper(this.pointLightRoom, this.pointLightRoom.distance);
        this.pointLightHelperPaper = new THREE.PointLightHelper(this.pointLightPaper, this.pointLightPaper.distance);

        this.scene.add(this.globalLight);
        this.container.add(this.pointLightNeighbor);
        this.container.add(this.pointLightRoom);
        this.container.add(this.pointLightPaper);
        this.container.add(this.pointLightHelperRoom);
        this.container.add(this.pointLightHelperPaper);
    }

    createMaterials() {
        // Easy to add more here if required
        const { hatchC, hatchD } = this.assets.textures;

        // OUTLINES
        const outlineCommon = { size: 0.0025, time: 0 }; // Size should be dependent on window.devicePixelRatio

        this.outlineMatBlack = new OutlineMaterial({ ...outlineCommon, color: 0x000000 });
        this.outlineMatWhite = new OutlineMaterial({ ...outlineCommon, size: 0.0015, color: 0xaaaaaa });

        // ROOM
        this.matRoom = new HatchingMaterial({
            hatchingMap: hatchC,
            parameters: { ...commonParams },
        });

        // Buildings
        this.matBuildings = new HatchingMaterial({
            hatchingMap: hatchC,
            parameters: {
                ...commonParams,
                hatchingScale: 40,
            },
        });

        // Deco
        this.matDeco = new HatchingMaterial({
            hatchingMap: hatchC,
            parameters: {
                ...commonParams,
                hatchingScale: 60,
                side: THREE.DoubleSide,
            },
        });

        // Deco
        this.matPaper = new HatchingMaterial({
            hatchingMap: hatchC,
            parameters: {
                ...commonParams,
                hatchingScale: 100,
                side: THREE.DoubleSide,
            },
        });

        // sky
        this.matSky = new HatchingMaterial({
            hatchingMap: hatchC,
            parameters: {
                ...commonParams,
                fog: false,
                hatchingScale: 50,
            },
        });

        // Persos 1
        this.matPersos1 = new HatchingMaterial({
            hatchingMap: hatchC,
            skinning: true,
            parameters: {
                ...commonParams,
                hatchingScale: 10,
                side: THREE.DoubleSide,
            },
        });

        // Persos 2
        this.matPersos2 = new HatchingMaterial({
            hatchingMap: hatchD,
            parameters: {
                ...commonParams,
                hatchingScale: 20,
                shadowDarkness: 0.3,
            },
        });
    }

    updateStep(p) {
        super.updateStep(p)

        const { TITLES, ROOM_CLOSE } = KafkaStep;

        if (this.oldStep == TITLES && this.currentStep == ROOM_CLOSE) {
            this.headTransitionSpeed = .1
        } else {
            this.headTransitionSpeed = 1
        }
    }


}